import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    Inject,
    Input,
    OnInit,
    Optional,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { MyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { CustomLoadingIndicatorService, LOADING_INDICATOR } from '@frontend/sports/loading-indicator-feature';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { LocalStoreService } from '@frontend/vanilla/core';

import { FreshDataService } from '../data-refresh/fresh-data.service';
import { HELP_BOX_TYPE } from '../help-box/help-box-type';
import { HelpBoxService } from '../help-box/help-box.service';
import { MyBetsEarlyPayoutService } from '../my-bets-base/early-payout/my-bets-earlypayout.service';
import { MyBetsEarlyPayoutServiceMyBetsFactory } from '../my-bets-base/early-payout/my-bets-earlypayout.service.factory';
import { IBetSlipRepositoryResult, MyBetsListContext } from '../my-bets-base/models/my-bets-viewmodels';
import { MyBetsFilterConstraint } from './models/my-bets-filter-constraints';
import { MyBetsBetslipRepository } from './services/my-bets-betslip.repository';
import { MainMyBetsStrategy, MyBetsStrategy } from './services/my-bets-display.strategy';
import { MyBetsEditBetHistoryService } from './services/my-bets-edit-bet-history.service';
import { MyBetsLoaderService } from './services/my-bets-loader.service';
import { MyBetsUtilService } from './services/my-bets-util.service';

@Component({
    selector: 'ms-my-bets-event-view',
    templateUrl: 'my-bets-event-view.html',
    providers: [{ provide: MyBetsStrategy, useClass: MainMyBetsStrategy }],
    standalone: false,
})
export class MyBetsEventViewComponent implements OnInit {
    @HostBinding('class') className = 'my-bets-event-view';

    private autoCashoutHelpBoxStorageKey = 'autocashoutHelpBoxClosed';
    private readonly destroyRef = inject(DestroyRef);
    private eventId = signal<EventModel | undefined>(undefined);
    private eventIds = computed(() => {
        const event = this.eventId();
        if (!event) {
            return [];
        }

        const id = event.id;
        const relatedTv2FixtureId = event.hybridFixtureData?.fixtureId || [];
        const betbuilderId = event.betBuilderTradingV2FixtureId ?? [];

        return this.loaderService.getGroupedEventIds(
            [id]
                .concat(event.splitFixtureIds || [])
                .concat(relatedTv2FixtureId || [])
                .concat(betbuilderId),
        );
    });

    myBetsListContext = computed(() => {
        const event = this.eventId();
        if (!event) {
            return { eventId: '', eventName: '', listType: undefined, isEventView: false } as MyBetsListContext;
        }

        return {
            eventId: event.id,
            eventName: event.name,
            listType: this.data.listType!,
            isEventView: true,
        } as MyBetsListContext;
    });

    isAutoCashoutHelpBoxVisible = computed(
        () =>
            this.helpBoxService.isHelpBoxEnabled(HELP_BOX_TYPE.AutoCashout) &&
            !this.localStorage.get(this.autoCashoutHelpBoxStorageKey) &&
            this.hasBetslips(),
    );

    filterConstraint = new MyBetsFilterConstraint();
    listType = BetslipListType.Open;
    isInitalLoadReady = false;
    hasBetslips = signal<boolean>(true);
    data: IBetSlipRepositoryResult;
    earlyPayoutService: MyBetsEarlyPayoutService;

    @Input()
    set event(value: EventModel) {
        this.eventId.set(value);
    }

    protected isEditBetHistory = computed(() => {
        return this.editBetHistoryService.hasEditBetHistoryBetslips;
    });

    constructor(
        private freshData: FreshDataService,
        private mybetsConfig: MyBetsConfig,
        private editBetHistoryService: MyBetsEditBetHistoryService,
        private repository: MyBetsBetslipRepository,
        private loadingIndicatorService: CustomLoadingIndicatorService,
        private loaderService: MyBetsLoaderService,
        protected helpBoxService: HelpBoxService,
        protected localStorage: LocalStoreService,
        protected trackingService: TrackingService,
        private changeRef: ChangeDetectorRef,
        public myBetsUtilService: MyBetsUtilService,
        earlyPayoutServiceFactory: MyBetsEarlyPayoutServiceMyBetsFactory,
        @Optional() @Inject(LOADING_INDICATOR) private token: string,
    ) {
        effect(() => {
            this.reload();
        });

        this.earlyPayoutService = earlyPayoutServiceFactory.create();
    }

    async ngOnInit(): Promise<void> {
        this.freshData.reloadNeeded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
    }

    async loadMoreBetSlips(): Promise<void> {
        const indicator = this.loadingIndicatorService.start(this.token);

        try {
            this.data = await this.repository.getNextBetslips({
                currentData: this.data,
                eventIds: this.eventIds(),
                pageSize: this.mybetsConfig.pageSize,
            });
            this.hasBetslips.set(this.data.betslips.length > 0);
        } catch {
        } finally {
            indicator.done();
        }
    }

    onBetPinned = (betslipId: string) => {
        this.repository.moveBetToTop(betslipId, this.data.betslips);
    };

    onBetUnpinned = async () => {
        await this.reload();
    };

    async reload(): Promise<void> {
        if (!this.eventId()) {
            return;
        }

        this.isInitalLoadReady = false;
        const indicator = this.loadingIndicatorService.start(this.token);

        try {
            this.data = await this.repository.getBetSlips({
                eventIds: this.eventIds(),
                pageSize: this.mybetsConfig.pageSize,
                listType: this.listType,
            });
            this.hasBetslips.set(this.data.betslips.length > 0);
        } finally {
            this.isInitalLoadReady = true;
            indicator.done();
        }
        this.changeRef.detectChanges();
    }

    onAutoCashoutHelpBoxClose = () => {
        this.localStorage.set(this.autoCashoutHelpBoxStorageKey, true);
        this.isAutoCashoutHelpBoxVisible = computed(() => false);
    };

    onAutoCashoutHelpInfoIconClick = () => {
        this.trackingService.track(trackingConstants.EVENT_AUTO_EARLYPAYOUT_SET, {
            [trackingConstants.COMPONENT_AUTO_EARLYPAYOUT]: 'AutoEarlyPayout_SetFlyOut_Info_Click_Main_MyBets',
        });
    };
}
